import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { PageHeader, Disclaimer } from "@/components/Common";
import { DocStatusBadge } from "@/components/StatusBadge";
import { DOC_TYPES, DOC_STATUSES } from "@/lib/constants";
import {
  Plus, MoreHorizontal, Eye, Pencil, Link2, CheckCircle2, Gavel, ArchiveRestore, ShieldCheck,
} from "lucide-react";
import { SEARCH_CATEGORIES } from "@/lib/lists";
import { SearchWithCategory, matchesSearch } from "@/components/Controlled";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import { formatNotes } from "@/lib/safeDisplay";

// MP8 — Documents list:
// 12 columns + quick actions. Searchable, sortable, filterable.
// Internal-only by default; no public document access.

const SORTABLE = {
  document_type: (a, b) => (a.document_type || "").localeCompare(b.document_type || ""),
  document_status: (a, b) => (a.document_status || "").localeCompare(b.document_status || ""),
  upload_date: (a, b) => (a.upload_date || a.updated_at || "").localeCompare(b.upload_date || b.updated_at || ""),
  uploaded_by: (a, b) => (a.uploaded_by || "").localeCompare(b.uploaded_by || ""),
};

export default function DocumentsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [items, setItems] = useState([]);
  const [tracks, setTracks] = useState({});
  const [requests, setRequests] = useState({});
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [searchCat, setSearchCat] = useState("all");
  const [sortKey, setSortKey] = useState("upload_date");
  const [sortDir, setSortDir] = useState("desc");

  async function load() {
    setLoading(true);
    try {
      const [d, t, r] = await Promise.all([
        api.get("/documents"),
        api.get("/tracks"),
        api.get("/license-requests").catch(() => ({ data: [] })),
      ]);
      setItems(d.data || []);
      const tMap = {}; (t.data || []).forEach(x => { tMap[x.id] = x; });
      const rMap = {}; (r.data || []).forEach(x => { rMap[x.id] = x; rMap[x.request_id] = x; });
      setTracks(tMap); setRequests(rMap);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let rows = items;
    if (type !== "all") rows = rows.filter(d => d.document_type === type);
    if (status !== "all") rows = rows.filter(d => d.document_status === status);
    const anyFields = ["document_name","linked_track_title","linked_catalog_id","document_type","linked_artist","linked_writer","linked_publisher","document_status","file_url","notes"];
    const resolver = (d, fid) => documentsFieldOf(d, fid, tracks, requests);
    rows = rows.filter(d => matchesSearch(d, q, searchCat, resolver, anyFields));
    const cmp = SORTABLE[sortKey] || SORTABLE.upload_date;
    rows = [...rows].sort((a, b) => sortDir === "asc" ? cmp(a, b) : cmp(b, a));
    return rows;
  }, [items, type, status, q, searchCat, sortKey, sortDir, tracks, requests]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  }

  async function patch(d, patch) {
    try {
      await api.put(`/documents/${d.id}`, { ...d, ...patch });
      toast.success("Document updated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div data-testid="documents-page">
      <PageHeader
        title="Documents"
        subtitle="Internal document tracking for rights-readiness and compliance review."
        actions={writeAccess ? (
          <Button onClick={() => navigate("/documents/new")} data-testid="documents-new-button" style={{ background: "hsl(347 72% 22%)" }}>
            <Plus className="w-4 h-4 mr-2" />New Document
          </Button>
        ) : (
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="documents-readonly-badge">Read-only access</span>
        )}
      />

      <Card className="mb-4">
        <CardContent className="p-4 space-y-3">
          <SearchWithCategory
            query={q}
            onQueryChange={setQ}
            category={searchCat}
            onCategoryChange={setSearchCat}
            categories={SEARCH_CATEGORIES.documents}
            placeholder="Search documents…"
            testidPrefix="documents"
          />
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[200px]">
              <label className="text-xs text-muted-foreground">Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="documents-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px]">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="documents-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {DOC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground self-end pb-2 ml-auto" data-testid="documents-count">{visible.length} of {items.length}</span>
          </div>
        </CardContent>
      </Card>

      {/* CISD posture note */}
      <Card className="mb-4 border-[hsl(40_48%_32%)]/30">
        <CardContent className="p-3 flex items-start gap-2 text-xs" data-testid="documents-cisd-note">
          <ShieldCheck className="w-4 h-4 mt-0.5" style={{ color: "hsl(40 48% 32%)" }} />
          <div>
            <span className="font-medium">CISD posture:</span> retain only necessary business identifiers in the live system where possible. Sensitive client materials should be handled as internal-only and may be archived offline when appropriate. No public document access.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document ID</TableHead>
                <TableHead>Linked Track</TableHead>
                <TableHead>Linked Request</TableHead>
                <SortableTH onClick={() => toggleSort("document_type")} active={sortKey === "document_type"} dir={sortDir}>Type</SortableTH>
                <SortableTH onClick={() => toggleSort("document_status")} active={sortKey === "document_status"} dir={sortDir}>Status</SortableTH>
                <TableHead>File name</TableHead>
                <SortableTH onClick={() => toggleSort("upload_date")} active={sortKey === "upload_date"} dir={sortDir}>Upload date</SortableTH>
                <SortableTH onClick={() => toggleSort("uploaded_by")} active={sortKey === "uploaded_by"} dir={sortDir}>Uploaded by</SortableTH>
                <TableHead>Internal</TableHead>
                <TableHead>Attorney</TableHead>
                <TableHead>Archived</TableHead>
                <TableHead className="w-[60px] sticky right-0 bg-card"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground">No documents match. Use New Document to add one.</TableCell></TableRow>
              )}
              {visible.map(d => {
                const t = tracks[d.linked_track_id];
                const r = requests[d.linked_request_id];
                return (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer hover:bg-secondary/40"
                    onClick={() => navigate(`/documents/${d.id}`)}
                    data-testid={`documents-row-${d.id}`}
                  >
                    <TableCell className="font-mono text-[11px]">{d.id.slice(0, 8)}…</TableCell>
                    <TableCell className="max-w-[160px] truncate text-sm">
                      {t ? <span>{t.track_title} <span className="text-xs text-muted-foreground">· {t.track_id}</span></span> : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm">
                      {r ? <span className="font-mono text-xs">{r.request_id}</span> : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="font-medium text-sm">{d.document_type}</TableCell>
                    <TableCell><DocStatusBadge status={d.document_status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{d.file_name || d.file_original_filename || "—"}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(d.upload_date || d.updated_at || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{d.uploaded_by || "—"}</TableCell>
                    <TableCell><IndicatorPill on={d.internal_only_indicator} on_label="Yes" off_label="No" testid={`doc-internal-${d.id}`} /></TableCell>
                    <TableCell><IndicatorPill on={d.attorney_review_indicator} on_label="Flagged" off_label="—" tone="bad" testid={`doc-attorney-${d.id}`} /></TableCell>
                    <TableCell><IndicatorPill on={d.archived_offline_indicator} on_label="Offline" off_label="—" tone="muted" testid={`doc-archived-${d.id}`} /></TableCell>
                    <TableCell className="sticky right-0 bg-card" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`documents-actions-${d.id}`} aria-label="Document actions">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[240px]">
                          <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => navigate(`/documents/${d.id}`)} data-testid={`documents-action-view-${d.id}`}>
                            <Eye className="w-4 h-4 mr-2" />View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/documents/${d.id}`)} data-testid={`documents-action-edit-${d.id}`}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/documents/${d.id}?link=1`)} data-testid={`documents-action-link-${d.id}`}>
                            <Link2 className="w-4 h-4 mr-2" />Link to Track
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem disabled={d.document_status === "Accepted"} onClick={() => patch(d, { document_status: "Accepted" })} data-testid={`documents-action-accepted-${d.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-2" />Mark Accepted
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => patch(d, { attorney_review_indicator: true, document_status: "Attorney Review Required" })} data-testid={`documents-action-attorney-${d.id}`}>
                            <Gavel className="w-4 h-4 mr-2" />Mark Attorney Review Required
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => patch(d, { archived_offline_indicator: true, document_status: "Archived Offline" })} data-testid={`documents-action-archived-${d.id}`}>
                            <ArchiveRestore className="w-4 h-4 mr-2" />Mark Archived Offline
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Documents are internal-only · CISD posture: minimize retained sensitive client materials · Not legal advice.
      </Disclaimer>
    </div>
  );
}

function SortableTH({ children, onClick, active, dir }) {
  return (
    <TableHead onClick={onClick} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span className="text-[10px] text-muted-foreground">{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </TableHead>
  );
}

function IndicatorPill({ on, on_label, off_label, tone, testid }) {
  if (!on) return <span data-testid={`${testid}-off`} className="text-[11px] text-muted-foreground">{off_label}</span>;
  const cls = tone === "bad" ? "status-attorney" : tone === "muted" ? "status-incomplete" : "status-client";
  return <span data-testid={`${testid}-on`} className={`inline-flex text-[11px] px-1.5 py-0.5 rounded ${cls}`}>{on_label}</span>;
}

function documentsFieldOf(d, fieldId, tracks, requests) {
  const t = tracks[d.linked_track_id] || {};
  switch (fieldId) {
    case "document_name":       return d.file_name || d.file_original_filename;
    case "linked_track_title":  return t.track_title || d.linked_track_title;
    case "linked_catalog_id":   return t.track_id || d.linked_track_id;
    case "document_type":       return d.document_type;
    case "linked_artist":       return t.artist;
    case "linked_writer":       return Array.isArray(t.writers) ? t.writers.join(", ") : (t.writers || "");
    case "linked_publisher":    return Array.isArray(t.publishers) ? t.publishers.join(", ") : (t.publishers || "");
    case "document_status":     return d.document_status;
    case "file_url":            return d.file_url || d.storage_path;
    case "notes":               return formatNotes(d.notes, { fallback: "" });
    default: return "";
  }
}

